import React, { useEffect, useState } from 'react';
import { Toast } from '../ui.jsx';
import Sidebar from '../components/Sidebar';
import SchoolBadge from '../components/SchoolBadge';
import GlobalSearch from '../components/GlobalSearch';
import NotificationBell from '../components/NotificationBell';
import AdminPopover from '../components/AdminPopover';

import Dashboard from './Dashboard';
import NewAdmissions from './NewAdmissions';
import Students from './Students';
import Teachers from './Teachers';
import StaffAttendancePage from './StaffAttendancePage';
import Classes from './Classes';
import Timetable from './Timetable';
import Attendance from './Attendance';
import Exams from './Exams';
import AcademicCalendar from './AcademicCalendar';
import Fees from './Fees';
import Documents from './Documents';
import Reports from './Reports';
import Settings from './Settings';
import NoticeBoard from './NoticeBoard';
import AdminMessages from '../AdminMessages';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';

function AdminApp({ db, save, page, setPage, user, setUser, onLogout, onSwitchSession, activeSessionId }) {
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  React.useEffect(() => {
    if (isMobile) setMobileNavOpen(false);
  }, [page, isMobile]);

  const pages = {
    dash: <Dashboard db={db} save={save} setPage={setPage} />,
    adm: <NewAdmissions />,
    stu: <Students db={db} save={save} setPage={setPage} activeSessionId={activeSessionId} />,
    tea: <Teachers db={db} save={save} />,
    satt: <StaffAttendancePage db={db} activeSessionId={activeSessionId} />,
    cls: <Classes db={db} save={save} />,
    tt: <Timetable db={db} save={save} />,
    att: <Attendance db={db} save={save} activeSessionId={activeSessionId} />,
    exam: <Exams db={db} save={save} logo={LOGO_SRC} activeSessionId={activeSessionId} />,
    cal: <AcademicCalendar db={db} save={save} />,
    fee: <Fees db={db} save={save} />,
    docs: <Documents db={db} save={save} activeSessionId={activeSessionId} />,
    rep: <Reports db={db} save={save} activeSessionId={activeSessionId} />,
    set: <Settings db={db} save={save} user={user} setUser={setUser} />,
    msg: <AdminMessages activeSessionId={activeSessionId} />,
    notices: <NoticeBoard />,
  };

  return (
    <div className="app-shell" style={{ display: 'flex', height: '100vh', background: '#f7fafc' }}>
      <Sidebar
        page={page}
        setPage={setPage}
        user={user}
        onLogout={onLogout}
        isMobile={isMobile}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      {isMobile && mobileNavOpen && (
        <div
          className="app-sidebar-overlay"
          onClick={() => setMobileNavOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100 }}
        />
      )}
      <div className="app-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Top header */}
        <header className="app-header" style={{
          height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 32px', position: 'relative', overflow: 'visible',
          background: '#ffffff',
          borderBottom: '1px solid #e8edf5',
          boxShadow: '0 2px 12px rgba(0,31,77,0.07)',
          zIndex: 100,
        }}>
          {/* Left — school name + aesthetic accent */}
          <div className="app-header-left"
            onClick={() => setPage('dash')}
            style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1, cursor: 'pointer' }}
            title="Go to Dashboard"
          >
            <button
              className="app-menu-btn"
              onClick={(e) => { e.stopPropagation(); setMobileNavOpen(v => !v); }}
              style={{ display: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: '#1960a3', padding: 4 }}
              aria-label="Toggle navigation menu"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>menu</span>
            </button>
            <SchoolBadge />
            {/* thin vertical divider + "Est. 2009" tag */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 1, height: 28, background: 'linear-gradient(180deg,transparent,#c7d9f5,transparent)' }} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: '#1960a3', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Est.</span>
                <span style={{ fontSize: 13, fontWeight: 900, color: '#002045', fontFamily: 'Manrope,sans-serif', letterSpacing: '0.04em' }}>2009</span>
              </div>
            </div>
          </div>
          {/* Right — search + actions */}
          <div className="app-header-right" style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
            <GlobalSearch db={db} setPage={setPage} isMobile={isMobile} />
            <button className="app-session-btn app-mobile-hide" onClick={onSwitchSession}
              style={{ background: '#eef4ff', color: '#1960a3', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #c7d9f5', letterSpacing: '0.04em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 150ms' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#eef4ff'; e.currentTarget.style.borderColor = '#c7d9f5'; }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>swap_horiz</span>
              {db.settings.year || '2025-26'}
            </button>
            <div className="app-mobile-hide"><NotificationBell db={db} setPage={setPage} /></div>
            <button className="app-print-btn" onClick={() => window.print()} style={{ color: '#64748b', border: 0, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', borderRadius: 8, padding: 6, transition: 'all 150ms' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#1960a3'; e.currentTarget.style.background = '#eef4ff'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; }}>
              <span className="material-symbols-outlined">print</span>
            </button>
            <div className="app-header-divider" style={{ width: 1, height: 32, background: '#e2e8f0' }} />
            <div className="app-mobile-tools" style={{ display: 'none', alignItems: 'center', gap: 8 }}>
              <button className="app-mobile-session-btn" onClick={onSwitchSession}
                style={{ background: '#eef4ff', color: '#1960a3', fontSize: 10, fontWeight: 800, padding: '6px 10px', borderRadius: 999, border: '1px solid #c7d9f5', letterSpacing: '0.06em', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, transition: 'all 150ms', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#eef4ff'; e.currentTarget.style.borderColor = '#c7d9f5'; }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>swap_horiz</span>
                {db.settings.year || '2025-26'}
              </button>
              <div className="app-mobile-bell"><NotificationBell db={db} setPage={setPage} /></div>
            </div>
            <AdminPopover user={user} setPage={setPage} onLogout={onLogout} isMobile={isMobile} />
          </div>
        </header>
        <div className="app-main flex-1 overflow-y-auto p-6" style={{
          background: '#f6f8fc',
          backgroundImage: `
            radial-gradient(circle at top left, rgba(25,96,163,0.08), transparent 34%),
            linear-gradient(180deg, rgba(255,255,255,0.75), rgba(246,248,252,1))
          `,
        }}>
          {pages[page] || <div className="text-on-surface-variant">Page not found</div>}
        </div>
      </div>
      <Toast />
    </div>
  );
}

export default AdminApp;
